import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { CourtChangeRequestService } from '@ee/common/services';
import { CourtChangeRequestKind, COURT_TYPES } from '@ee/common/enums';
import { Court, CourtChangeRequestSubmission, CourtChangeRequestSubmissionResult } from '@ee/common/models';

/**
 * Data handed to the court-change-request dialog by its opener (the court picker, task 15.2).
 *
 * `courts` are the courts already presented in the picker — the choices a COURT_UPDATE targets. `state`
 * and `countyOrVenue` seed the form from the surrounding case context so the requester does not retype
 * what the picker already knows. `selectedCourtId` is the court currently chosen in the picker, preselected
 * as the court to correct.
 */
export interface CourtChangeRequestDialogData {
  courts?: Court[];
  selectedCourtId?: string;
  state?: string;
  countyOrVenue?: string;
}

/** The court fields a requester can supply/change; used to prefill an update and diff it on submit. */
const EDITABLE_COURT_FIELDS = [
  'name',
  'type',
  'street_address',
  'street_address_2',
  'city',
  'state',
  'zip_code',
  'phone_number',
  'precinct',
  'place',
  'district'
] as const;

/** The court-picker value meaning "my court isn't listed" — selecting it switches to a NEW_COURT request. */
const NEW_COURT_CHOICE = '__new_court__';

/** Fields a NEW_COURT cannot be submitted without — the backend rejects the request if any is blank. */
const NEW_COURT_REQUIRED_FIELDS = ['name', 'type', 'state', 'county_or_venue'] as const;

/**
 * Trim-aware required: `Validators.required` accepts a whitespace-only string (its length is non-zero),
 * which would let a blank Source URL through and then submit as an empty value once trimmed. Report the
 * standard `required` error for a value that is empty after trimming.
 */
function requiredTrimmed(control: AbstractControl): ValidationErrors | null {
  // String() so a non-string control (the court-type select) is handled too.
  return String(control.value ?? '').trim().length > 0 ? null : { required: true };
}

/** A citation URL must be an http(s) address — a bare word is not evidence anyone can check. */
function httpUrl(control: AbstractControl): ValidationErrors | null {
  const value = (control.value ?? '').trim();
  if (!value) {
    return null; // `requiredTrimmed` owns the empty case so we don't double-report.
  }
  return /^https?:\/\/.+/i.test(value) ? null : { url: true };
}

/**
 * In-app court change request modal (EE-386, Req 7.2–7.4/7.6/7.8).
 *
 * A requester either picks an existing court to correct or chooses to add a new one, supplies the
 * changed/new details and a required Source URL, and submits. Submitting only opens a PENDING request
 * for admin review — the dialog confirms it was "submitted for review" and never implies the court
 * changed immediately (Req 7.6). The opener gates who can reach this dialog (Req 7.5/8.2); the dialog
 * itself just captures and submits.
 */
@Component({
  selector: 'ee-court-change-request-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    NgxMaskDirective
  ],
  providers: [provideNgxMask()],
  templateUrl: './court-change-request-dialog.component.html',
  styleUrls: ['./court-change-request-dialog.component.scss']
})
export class CourtChangeRequestDialogComponent {
  public readonly Kind = CourtChangeRequestKind;
  public readonly courtTypes = COURT_TYPES;
  public readonly courts: Court[];
  public readonly NEW_COURT_CHOICE = NEW_COURT_CHOICE;

  /**
   * The single court picker: an existing court (a COURT_UPDATE targeting it) or {@link NEW_COURT_CHOICE}.
   * Kept outside `form` — it only drives `request_kind`/`target_court_id`, which are what get submitted.
   */
  public readonly courtChoice = new FormControl<string | null>(null);

  public readonly form: FormGroup;

  /** True once the request has been accepted for review — the dialog then shows only the confirmation. */
  public submitted = false;
  public submitting = false;
  public errorMessage: string | null = null;

  /** The server's user-facing confirmation, shown after a successful submit. */
  public confirmationMessage = '';

  private selectedCourt: Court | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly service: CourtChangeRequestService,
    private readonly dialogRef: MatDialogRef<CourtChangeRequestDialogComponent, CourtChangeRequestSubmissionResult>,
    private readonly cdr: ChangeDetectorRef,
    @Inject(MAT_DIALOG_DATA) public readonly data: CourtChangeRequestDialogData
  ) {
    this.courts = data?.courts ?? [];

    this.form = this.fb.group({
      request_kind: [CourtChangeRequestKind.COURT_UPDATE],
      target_court_id: [''],
      name: [''],
      type: [null],
      street_address: [''],
      street_address_2: [''],
      city: [''],
      state: [data?.state ?? ''],
      zip_code: [''],
      phone_number: [''],
      precinct: [''],
      place: [''],
      district: [''],
      county_or_venue: [data?.countyOrVenue ?? ''],
      source_url: ['', [requiredTrimmed, httpUrl]]
    });

    // No courts to correct → default to adding a new one so the modal is never stuck on an empty select.
    if (this.courts.length === 0) {
      this.form.get('request_kind').setValue(CourtChangeRequestKind.NEW_COURT);
      this.courtChoice.setValue(NEW_COURT_CHOICE);
    }
    this.applyKindValidators(this.form.get('request_kind').value);

    // Preselect the court the picker already has, so correcting it is one step.
    const preselected = this.courts.find((c) => !!c.id && c.id === data?.selectedCourtId);
    if (preselected) {
      this.onCourtChoice(preselected.id);
    }
  }

  /** Whether the requester has picked a court or "not listed" — the detail fields show only then. */
  public get hasChoice(): boolean {
    return !!this.courtChoice.value;
  }

  /** Map the single court picker onto the request kind and its target court. */
  public onCourtChoice(choice: string): void {
    this.courtChoice.setValue(choice, { emitEvent: false });
    const kind = choice === NEW_COURT_CHOICE ? CourtChangeRequestKind.NEW_COURT : CourtChangeRequestKind.COURT_UPDATE;
    this.form.get('request_kind').setValue(kind);
    this.onKindChange(kind);
    if (kind === CourtChangeRequestKind.COURT_UPDATE) {
      this.form.get('target_court_id').setValue(choice);
      this.onTargetCourtChange(choice);
    }
  }

  public get isUpdate(): boolean {
    return this.form.get('request_kind').value === CourtChangeRequestKind.COURT_UPDATE;
  }

  /** Whether the current input is enough to submit — mirrors the disabled state of the SUBMIT button. */
  public get canSubmit(): boolean {
    if (this.form.invalid || this.submitting) {
      return false;
    }
    return this.isUpdate ? !!this.selectedCourt && this.hasUpdateChanges() : true;
  }

  public onKindChange(kind: CourtChangeRequestKind): void {
    this.applyKindValidators(kind);
    if (kind === CourtChangeRequestKind.NEW_COURT) {
      this.selectedCourt = null;
      this.form.get('target_court_id').setValue('');
      // A court picked before the switch prefilled the editable fields; a NEW_COURT serializes every
      // populated field, so leaving them would submit that court's details as a duplicate new court.
      // Clear the court-specific fields, keeping only the case-context seed (state).
      this.form.patchValue({
        name: '',
        type: null,
        street_address: '',
        street_address_2: '',
        city: '',
        state: this.data?.state ?? '',
        zip_code: '',
        phone_number: '',
        precinct: '',
        place: '',
        district: ''
      });
    }
  }

  public onTargetCourtChange(courtId: string): void {
    this.selectedCourt = this.courts.find((c) => (c.id ?? '') === courtId) ?? null;
    // Prefill the current values so the requester edits them in place and we can diff what changed.
    if (this.selectedCourt) {
      const court = this.selectedCourt;
      this.form.patchValue({
        name: court.name ?? '',
        type: court.type ?? null,
        street_address: court.street_address ?? '',
        street_address_2: court.street_address_2 ?? '',
        city: court.city ?? '',
        state: court.state ?? '',
        zip_code: court.zip_code ?? '',
        phone_number: court.phone_number ?? '',
        precinct: court.precinct ?? '',
        place: court.place ?? '',
        district: court.district ?? ''
      });
    }
  }

  public submit(): void {
    if (!this.canSubmit) {
      return;
    }
    this.submitting = true;
    this.errorMessage = null;
    // Block Escape/backdrop dismissal while the POST is in flight: a dismissal mid-request would still
    // create the request (server-side) but hide the confirmation, and reopening could submit a duplicate.
    this.dialogRef.disableClose = true;

    this.service.submit(this.buildSubmission()).subscribe({
      next: (result) => {
        this.submitting = false;
        this.dialogRef.disableClose = false;
        this.submitted = true;
        this.confirmationMessage =
          result?.message?.trim() ||
          'Your request was submitted for review. Our team will review it before any court is changed.';
        // OnPush: the HTTP response is not an input/template-event/async-pipe change, so mark the
        // view dirty ourselves or the spinner/confirmation would not repaint (ui-conventions.md).
        this.cdr.markForCheck();
      },
      error: () => {
        this.submitting = false;
        this.dialogRef.disableClose = false;
        this.errorMessage = 'We could not submit your request. Please try again.';
        this.cdr.markForCheck();
      }
    });
  }

  /** Close and hand the successful result back to the opener. */
  public done(): void {
    this.dialogRef.close();
  }

  private applyKindValidators(kind: CourtChangeRequestKind): void {
    // A NEW_COURT needs a name, type, state and county/venue: the backend rejects a submission without
    // them (approval cannot create and link a court otherwise). A COURT_UPDATE needs none of them.
    const newCourtRequired = NEW_COURT_REQUIRED_FIELDS.map((field) => this.form.get(field));
    const target = this.form.get('target_court_id');
    if (kind === CourtChangeRequestKind.NEW_COURT) {
      // Trim-aware so a whitespace-only value can't pass required and then be dropped by the trimming
      // submission builder, sending a NEW_COURT without it (mirrors the source_url treatment).
      newCourtRequired.forEach((control) => control.setValidators([requiredTrimmed]));
      target.clearValidators();
    } else {
      target.setValidators([Validators.required]);
      newCourtRequired.forEach((control) => control.clearValidators());
    }
    [...newCourtRequired, target].forEach((control) => control.updateValueAndValidity({ emitEvent: false }));
  }

  /** For an update, at least one court field must be a submittable change (see {@link isFieldChanged}). */
  private hasUpdateChanges(): boolean {
    if (!this.selectedCourt) {
      return false;
    }
    return EDITABLE_COURT_FIELDS.some((field) => this.isFieldChanged(field));
  }

  /**
   * A field is a submittable update only when the requester supplied a non-empty value that differs
   * from the court's current value. Blanking a populated field is deliberately NOT a change: the
   * backend applies `requested_values` as a targeted `$set` (set-only — it cannot represent a clear),
   * and the submission omits `undefined`, so a blanked field would silently drop out and could leave
   * an empty update that `canSubmit` had reported as valid. Keeping {@link hasUpdateChanges} and
   * {@link buildSubmission} on this one predicate stops them diverging. Supporting an explicit field
   * clear is a backend-contract change, tracked separately (EE-386 review).
   */
  private isFieldChanged(field: string): boolean {
    const court = (this.selectedCourt ?? {}) as unknown as Record<string, string | null | undefined>;
    const next = this.normalize(this.form.get(field).value);
    return next.length > 0 && next !== this.normalize(court[field]);
  }

  /**
   * Build the flat submission the backend expects: every populated field for a NEW_COURT, and only the
   * changed fields (against the selected court) for a COURT_UPDATE, so requested_values reflects the
   * actual change rather than the whole prefilled form.
   */
  private buildSubmission(): CourtChangeRequestSubmission {
    const value = this.form.getRawValue();
    const fields: Record<string, string | undefined> = {};

    if (this.isUpdate) {
      EDITABLE_COURT_FIELDS.forEach((field) => {
        if (this.isFieldChanged(field)) {
          fields[field] = this.fieldValue(value[field]);
        }
      });
    } else {
      EDITABLE_COURT_FIELDS.forEach((field) => {
        const fieldValue = this.fieldValue(value[field]);
        if (fieldValue !== undefined) {
          fields[field] = fieldValue;
        }
      });
    }

    return {
      request_kind: value.request_kind,
      source_url: (value.source_url ?? '').trim(),
      state: this.trimToUndefined(value.state),
      county_or_venue: this.trimToUndefined(value.county_or_venue),
      target_court_id: this.isUpdate ? value.target_court_id : undefined,
      ...fields
    };
  }

  private fieldValue(value: unknown): string | undefined {
    if (typeof value === 'string') {
      return this.trimToUndefined(value);
    }
    return (value as string | undefined) ?? undefined;
  }

  private trimToUndefined(value: string | null | undefined): string | undefined {
    const trimmed = (value ?? '').trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  /** Every court field compared here is string-valued (including the CourtType enum), so a trimmed
   * string / empty comparison is sufficient — no non-string coercion is needed. */
  private normalize(value: string | null | undefined): string {
    return (value ?? '').trim();
  }
}
